import React, { useState, useEffect, useContext, useRef } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import classNames from 'classnames'
import Tooltip from '~/components/common/Tooltip'
import withSimpleApi, { WithSimpleApiProps } from '~/decorators/withSimpleApi'
import { formatDate } from '~/utils/dateFns'
import Parser from 'html-react-parser'
import ChatInput from './Input'
import { post } from '~/utils/api'
import { ChatContext } from '~/service/chatConn'
import { VariableSizeList as List } from 'react-window'
import useResizeObserver from 'use-resize-observer'

type Message = {
  id: number
  content: string
  createdAt: { date: Date }
  me: boolean
  read: boolean
}

type AddMessage = {
  chatId: number
  message: Message
}

type State = {
  messages: Message[]
}

const Show: React.FC<WithSimpleApiProps<State>> = ({ apiData }) => {
  const { isConnected, channel: chatChannel } = useContext(ChatContext)
  const history = useHistory()
  const [messages, setMessages] = useState<Message[]>(apiData.messages)
  const { chatId } = useParams<{ chatId: string }>()
  const user = window.c.users.find(user => user.id == Number(chatId))
  const me = window.c.user

  const messagesRef = useRef(messages)
  const listRef = useRef<any>({})
  const rowHeights = useRef({})

  messagesRef.current = messages

  const { ref, height = 0 } = useResizeObserver<HTMLElement>()

  const setRowHeight = (index, size) => {
    listRef.current.resetAfterIndex(0)
    rowHeights.current = { ...rowHeights.current, [index]: size }
  }

  const getRowHeight = index => {
    return rowHeights.current[index] || 50
  }

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom()
    }
  }, [messages])

  const addMessage = async (content: string) => {
    await post(`internal-chat/send`, {
      content: content,
      userId: chatId,
    })
  }

  const readMessages = async () => {
    const unreadMessages = messages.filter(
      message => !message.read && !message.me
    )

    await post(`internal-chat/read`, {
      ids: unreadMessages.map(message => message.id),
    })
  }

  useEffect(() => {
    readMessages()
  }, [messages])

  useEffect(() => {
    if (!isConnected) {
      return
    }

    chatChannel.on('internalChatMessage', (data: AddMessage) => {
      if (data.chatId == Number(chatId)) {
        setMessages([...messagesRef.current, data.message])
      }
    })
  }, [isConnected])

  const scrollToBottom = () => {
    listRef.current.scrollToItem(messages.length - 1, 'end')
  }

  const Row = ({ data: { messages }, index, style }) => {
    const rowRef: any = useRef({})

    useEffect(() => {
      if (rowRef.current) {
        setRowHeight(index, rowRef.current.clientHeight)
      }
    }, [rowRef])

    return (
      <>
        <div style={style} className="list-row">
          <div className="chat-output-row" ref={rowRef}>
            <div
              key={messages[index].id}
              className={classNames('chat-message chat-history-message', {
                'is-answer': messages[index].me,
                'is-question': !messages[index].me,
              })}
            >
              <Tooltip content={messages[index].me ? me.name : user?.name}>
                <div className="chat-history-message-avatar">
                  <img src={messages[index].me ? me.avatar : user?.avatar} />
                </div>
              </Tooltip>
              <Tooltip
                content={formatDate(messages[index].createdAt.date, 'PPp')}
              >
                <span className="chat-history-message-content">
                  {Parser(messages[index].content)}
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="chat-activeChat-main">
      <div className="chat-activeChat-header">
        <button
          onClick={() => {
            history.push('/internal-chat')
          }}
          className="chat-activeChat-header-back"
        >
          <span className="far fa-chevron-left" />
        </button>
        <figure className="chat-activeChat-header-avatar">
          <img src={user?.avatar} />
        </figure>
        <div className="chat-activeChat-header-name">{user?.name}</div>
      </div>
      <div id="output" className="chat-output" ref={ref}>
        <List
          className="chat-output-list"
          itemCount={messages.length}
          itemSize={getRowHeight}
          height={height}
          ref={listRef}
          itemData={{ messages: messages }}
        >
          {Row}
        </List>
      </div>
      <ChatInput addMessage={addMessage} />
    </div>
  )
}

const ShowWithApi = withSimpleApi(Show)

export default ShowWithApi
